import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from './client';

// Server: `record KeeperDto(int bodyPreset, int skinTone)` — 1-4 / 1-8 respectively
// (docs/RULINGS.md). Indices into a fixed preset list, not free-form strings.
export type KeeperDto = {
  bodyPreset: number;
  skinTone: number;
};

export const keeperKeys = {
  all: ['keeper'] as const,
};

// GET /keeper answers 200 with an EMPTY body before the keeper's first save (QA 2026-09-23 —
// it used to 404 `keeper_not_created`, a console error on every Me/profile load); apiFetch
// resolves an empty 2xx to `null`.
export const getKeeper = () => apiFetch<KeeperDto | null>('/keeper');

export const saveKeeper = (input: KeeperDto) =>
  apiFetch<KeeperDto>('/keeper', { method: 'PUT', body: JSON.stringify(input) });

/** Before the keeper's first save the server answers an empty 200, which `getKeeper` already
 *  resolves to `null`. The `keeper_not_created` catch below is kept for any server still
 *  serving the old 404 (and for the unit tests that mock it) — same `data: null`, either way. */
export function useKeeper() {
  return useQuery({
    queryKey: keeperKeys.all,
    queryFn: async (): Promise<KeeperDto | null> => {
      try {
        return await getKeeper();
      } catch (err) {
        if (err instanceof ApiClientError && err.code === 'keeper_not_created') return null;
        throw err;
      }
    },
  });
}

export function useSaveKeeper() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveKeeper,
    onSuccess: (data) => queryClient.setQueryData(keeperKeys.all, data),
  });
}
